import { Component, h, Host, Element, Prop, forceUpdate } from '@stencil/core';
import { alert_exclamation_circle, angle_down } from 'pn-design-assets/pn-assets/icons.js';
import { uuidv4 } from '@/index';

/**
 * The `pn-select` uses a native `select` element under the hood.
 * Use the slot to include `option` elements.
 *
 * Just like a regular `select`, you can use the `selected` prop on the nested option elements to preselect an option.
 *
 * @nativeChange Use the `change` event to listen to changes on the select element.
 */
@Component({
  tag: 'pn-select',
  styleUrl: 'pn-select.scss',
})
export class PnSelect {
  id: string = `pn-select-${uuidv4()}`;
  idHelpertext: string = `${this.id}-text`;

  select: HTMLSelectElement;

  mo: MutationObserver;

  @Element() hostElement: HTMLElement;

  /** Label placed above the select. */
  @Prop() label!: string;
  /** Display a helper text underneath the select. */
  @Prop() helpertext?: string;
  /** Select HTML id */
  @Prop() selectId: string = this.id;
  /** Display an icon to the left of the select input. */
  @Prop() icon?: string;

  /** HTML select name. @category HTML */
  @Prop() name: string;
  /** HTML form name. @category HTML */
  @Prop() form: string;
  /** HTML autocomplete. @category HTML */
  @Prop() autocomplete: string;
  /**
   * Set the select as required.
   * @category Validation
   */
  @Prop() required?: boolean = false;
  /**
   * Disable the select.
   * @category Validation
   */
  @Prop() disabled?: boolean = false;
  /**
   * Trigger the invalid state.
   * @category Validation
   */
  @Prop() invalid?: boolean = false;
  /**
   * Display an error message and trigger the invalid state.
   * @category Validation
   */
  @Prop() error?: string;

  componentDidLoad() {
    if (this.mo) this.mo.disconnect();

    this.mo = new MutationObserver(() => forceUpdate(this.hostElement));

    this.mo.observe(this.hostElement, { childList: true, subtree: true });
  }

  hasError() {
    return this.invalid || !!this.error;
  }

  hasMessage() {
    return !!this.helpertext || !!this.error;
  }

  render() {
    return (
      <Host>
        <div class="pn-select" data-error={this.hasError()}>
          {this.label && (
            <label htmlFor={this.selectId} class="pn-select-label">
              <span>{this.label}</span>
            </label>
          )}
          <div class="pn-select-input">
            {this.icon && (
              <pn-icon class="pn-select-icon" icon={this.icon} color="gray900" data-custom aria-hidden="true" />
            )}

            {this.hasError() && (
              <pn-icon
                class="pn-select-icon"
                icon={alert_exclamation_circle}
                color="warning"
                data-error
                aria-hidden="true"
              />
            )}

            <pn-icon class="pn-select-icon" icon={angle_down} color="blue700" data-default aria-hidden="true" />

            <select
              id={this.selectId}
              class="pn-select-element"
              name={this.name}
              form={this.form}
              autocomplete={this.autocomplete}
              aria-describedby={this.hasMessage() && this.idHelpertext}
              aria-invalid={this.hasError().toString()}
              disabled={this.disabled}
              data-placeholder={this.select?.value === ''}
              ref={el => (this.select = el)}
            >
              <slot />
            </select>
          </div>

          {this.hasMessage() && (
            <p id={this.idHelpertext} class="pn-select-helper" role={!!this.error ? 'alert' : null}>
              <span>{this.error || this.helpertext}</span>
            </p>
          )}
        </div>
      </Host>
    );
  }
}
